import { match, type V2BundleFragment } from "@stepapp/core";
import { CategoryType } from "@stepapp/core/apiTypes";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { any, array, boolean, number, object, string, unknown, ZodError, ZodIssueCode } from "zod";
import { AppStorage } from "../services/AppStorage";

type Props = {
  service?: V2BundleFragment;
  checkProperty: boolean;
  questionId: string;
};

export const useServiceFormSchemas = ({ service, checkProperty = false, questionId }: Props) => {
  const { t } = useTranslation();
  const hasPeriodicity = service?.subscriptionOptions && service?.subscriptionOptions?.length > 0;
  const hasHomeSize = !!service?.houseArea?.enabled;
  const countryCode = AppStorage().get("countryCode") || "PL";
  const { taxIdMin, postalCodeMin } = match(countryCode)
    .with("NO", () => ({ taxIdMin: 10, postalCodeMin: 4 }))
    .otherwise(() => ({ taxIdMin: 12, postalCodeMin: 6 }));

  const requirePresenceInfo = service?.categoryType !== CategoryType.Massage;

  const maxHomeSize =
    service?.houseArea?.steps?.reduce(
      (max, step) => {
        return step?.to > max.to ? step : max;
      },
      { to: 0 },
    )?.to || 500;

  const introSchema = useMemo(
    () =>
      object({
        subscriptionOptionId: hasPeriodicity ? string().min(1, t("validation.pickOption")) : any(),
        attributes: array(object({ attributeId: string(), quantity: number() })).superRefine(
          (val, ctx) => {
            const requiredAnswerIds =
              service?.questions
                ?.filter((question) => question?.isRequired)
                ?.map((question) => question?.id) ?? [];

            const requiredCategoryAnswerIds =
              service?.categories?.flatMap((category) =>
                category?.questions
                  ?.filter((question) => question?.isRequired)
                  ?.map((question) => question?.id),
              ) ?? [];

            const requiredIds = [...requiredCategoryAnswerIds, ...requiredAnswerIds];

            const hasAllRequired = requiredIds?.every((requiredAnswerId) =>
              val?.some(({ attributeId }) => requiredAnswerId === attributeId),
            );

            if (!hasAllRequired) {
              ctx.addIssue({
                code: ZodIssueCode.custom,
                message: t("validation.pickOption"),
              });
            }
          },
        ),
        address: object({
          streetAddress1: checkProperty ? any() : string().min(1, t("validation.address")),
        }),
        propertyId: checkProperty ? string().min(1) : string().optional().nullable(),
        services: any(),
        houseArea: checkProperty
          ? number().max(maxHomeSize, t("validation.maxSurface", { maxSize: maxHomeSize }))
          : hasHomeSize
            ? number()
                .min(10, t("validation.minSurface", { minSize: 10 }))
                .max(maxHomeSize, t("validation.maxSurface", { maxSize: maxHomeSize }))
            : any(),
      }),
    [service, maxHomeSize, hasPeriodicity, hasHomeSize, checkProperty, t],
  );

  const dateAndAddressSchema = useMemo(
    () =>
      object({
        address: object({
          city: string().optional().nullable(),
          doorCode: string().optional().nullable(),
          floorArea: number()
            .min(10, t("validation.minSurface", { minSize: 10 }))
            .max(maxHomeSize, t("validation.maxSurface", { maxSize: maxHomeSize })),
          notes: string().optional().nullable(),
          postalOrZipCode: string().min(postalCodeMin, t("validation.postalCode")),
          streetAddress1: string().min(1, t("validation.address")),
          streetAddress2: string().optional().nullable(),
          type: string().optional().nullable(),
        }),
        houseArea: number()
          .min(10, t("validation.minSurface", { minSize: 10 }))
          .max(maxHomeSize, t("validation.maxSurface", { maxSize: maxHomeSize })),
        meta: object({
          homeTypePlaceholder: requirePresenceInfo
            ? string().min(1, t("validation.homeType"))
            : any(),
          keyInfo: string(),
          willCustomerBePresent: requirePresenceInfo
            ? unknown().refine((value) => value !== "deselected", t("validation.pickOption"))
            : any(),
        }).superRefine((values, ctx) => {
          if (values.willCustomerBePresent === false && values.keyInfo.length < 3) {
            ctx.addIssue({
              code: ZodIssueCode.custom,
              path: ["keyInfo"],
              message: t("validation.keyInfo"),
            });
          }
        }),
        startDate: string().min(1, t("validation.date")),
      }),
    [t, postalCodeMin, requirePresenceInfo, maxHomeSize],
  );

  const addAddress = useMemo(
    () =>
      object({
        address: object({
          city: string().optional().nullable(),
          doorCode: string().optional().nullable(),
          floorArea: number()
            .min(10, t("validation.minSurface", { minSize: 10 }))
            .max(maxHomeSize, t("validation.maxSurface", { maxSize: maxHomeSize })),
          notes: string().optional().nullable(),
          postalOrZipCode: string().min(postalCodeMin, t("validation.postalCode")),
          streetAddress1: string().min(1, t("validation.address")),
          streetAddress2: string().optional().nullable(),
          type: string().optional().nullable(),
        }),
        meta: object({
          homeTypePlaceholder: requirePresenceInfo
            ? string().min(1, t("validation.homeType"))
            : any(),
          keyInfo: string(),
          willCustomerBePresent: requirePresenceInfo
            ? unknown().refine((value) => value !== "deselected", t("validation.pickOption"))
            : any(),
        }).superRefine((values, ctx) => {
          if (values.willCustomerBePresent === false && values.keyInfo.length < 3) {
            ctx.addIssue({
              code: ZodIssueCode.custom,
              path: ["keyInfo"],
              message: t("validation.keyInfo"),
            });
          }
        }),
      }),
    [t, postalCodeMin, requirePresenceInfo, maxHomeSize],
  );

  const billingAddressSchema = useMemo(
    () =>
      object({
        name: string().min(1, t("validation.companyName")),
        taxIdentification: string().length(taxIdMin, t("validation.nipNumber")),
        line1: string().min(1, t("validation.address")),
        line2: string().optional().nullable(),
        postal_code: string().length(postalCodeMin, t("validation.postalCode")),
        city: string().min(1, t("validation.city")),
        email: string().optional().nullable(),
      }),
    [t, taxIdMin, postalCodeMin],
  );

  const pickPropertySchema = useMemo(
    () =>
      object({
        propertyId: string().min(1),
        houseArea: number().max(maxHomeSize, t("validation.maxSurface", { maxSize: maxHomeSize })),
      }),
    [maxHomeSize, t],
  );

  const serviceQuestionSchema = useMemo(
    () =>
      object({
        address: object({
          streetAddress1: checkProperty ? any() : string().min(1, t("validation.address")),
        }),
        houseArea: checkProperty
          ? any()
          : hasHomeSize
            ? number()
                .min(10, t("validation.minSurface", { minSize: 10 }))
                .max(maxHomeSize, t("validation.maxSurface", { maxSize: maxHomeSize }))
            : any(),
        attributes: array(object({ attributeId: string(), quantity: number() })).superRefine(
          (val, ctx) => {
            const findIssue = () => {
              const currentCategory = service?.categories?.find(({ id }) => id === questionId);
              if (currentCategory) {
                const requiredCategoryQuestions = currentCategory?.questions?.filter(
                  (question) => question?.isRequired === true,
                );
                const requiredCategoryQuestionsIds = requiredCategoryQuestions?.map(
                  (question) => question?.id,
                );
                const hasAllRequired = requiredCategoryQuestionsIds?.every((requiredQuestionId) =>
                  val?.some(({ attributeId }) => requiredQuestionId === attributeId),
                );
                return !hasAllRequired;
              }
              const currentAttribute = service?.questions?.find(({ id }) => id === questionId);
              const answer = val?.find(({ attributeId }) => attributeId === questionId);
              const hasNoAnswer = !answer || answer?.quantity <= 0;
              const hasIssue = currentAttribute?.isRequired && hasNoAnswer;

              return hasIssue;
            };

            const hasIssue = findIssue();

            if (hasIssue) {
              ctx.addIssue({
                code: ZodIssueCode.custom,
                message: t("validation.pickOption"),
              });
            }
          },
        ),
        subscriptionOptionId:
          hasPeriodicity && questionId === "subscription"
            ? string().min(1, t("validation.pickOption"))
            : any(),
        services: any(),
      }),
    [
      hasPeriodicity,
      questionId,
      service?.questions,
      service?.categories,
      checkProperty,
      hasHomeSize,
      maxHomeSize,
      t,
    ],
  );

  const summarySchema = useMemo(
    () =>
      object({
        billingAddress: any(),
        meta: object({ addInvoice: boolean() }),
      }).superRefine((values, ctx) => {
        if (!values.meta.addInvoice) return;

        try {
          billingAddressSchema.parse(values.billingAddress);
        } catch (err) {
          if (err instanceof ZodError) {
            for (const issue of err.issues) {
              const newIssue = {
                ...issue,
                path: [`billingAddress.${issue.path[0]}`],
                code: ZodIssueCode[issue.code],
              };
              // @ts-expect-error
              ctx.addIssue(newIssue);
            }
          }
        }
      }),
    [billingAddressSchema],
  );

  return useMemo(
    () => ({
      configure: introSchema,
      "date-and-address": dateAndAddressSchema,
      confirmation: any(),
      date: object({ startDate: string().min(1, t("validation.date")) }),
      "add-address": addAddress,
      "pick-property": pickPropertySchema,
      "[service-question]": serviceQuestionSchema,
      checklist: any(),
      summary: summarySchema,
    }),
    [
      introSchema,
      dateAndAddressSchema,
      addAddress,
      serviceQuestionSchema,
      summarySchema,
      pickPropertySchema,
      t,
    ],
  );
};
